import { apiClient } from './apiClient';
import { toast } from 'react-hot-toast';
import type { AdminUser } from './admin.service';
import type {
  Contact,
  ContactSource,
  ContactStatus,
  CrmNote,
  CrmReport,
  CrmSummary,
  OutreachChannel,
  OutreachLog,
  OutreachOutcome,
  OutreachSource,
  Reminder,
  ReminderStatus,
} from '@/types/crm.types';

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface Paginated<T> {
  items: T[];
  pagination: Pagination;
}

/**
 * CRM Service — calls the backend at /admin/crm/*.
 * Signatures kept stable so UI callers don't change.
 * On API error, surfaces a toast and throws so awaiting callers stop their flow.
 */

const buildQuery = (params: Record<string, string | number | undefined | null>) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.append(k, String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : '';
};

const unwrap = <T>(
  res: { success: true; data: T } | { success: false; error: { code: number | string; message: string } },
  fallbackMsg = 'CRM request failed'
): T => {
  if (res.success) return res.data;
  const msg = res.error?.message || fallbackMsg;
  toast.error(msg);
  throw new Error(msg);
};

export const crmService = {
  // ---------- Outreach ----------
  async listOutreach(
    userIdOrParams?:
      | string
      | {
          userId?: string;
          contactId?: string;
          source?: OutreachSource;
          channel?: OutreachChannel;
          page?: number;
          limit?: number;
        }
  ): Promise<OutreachLog[]> {
    const page = await crmService.listOutreachPage(userIdOrParams);
    return page.items;
  },

  async listOutreachPage(
    userIdOrParams?:
      | string
      | {
          userId?: string;
          contactId?: string;
          source?: OutreachSource;
          channel?: OutreachChannel;
          page?: number;
          limit?: number;
        }
  ): Promise<Paginated<OutreachLog>> {
    const params =
      typeof userIdOrParams === 'string'
        ? { userId: userIdOrParams }
        : userIdOrParams || {};
    const res = await apiClient.get<Paginated<OutreachLog>>(
      `/admin/crm/outreach${buildQuery(params)}`
    );
    return unwrap(res, 'Failed to load outreach');
  },

  async createOutreach(input: {
    userId?: string;
    contactId?: string;
    channel: OutreachChannel;
    source: 'manual';
    outcome: OutreachOutcome;
    message?: string;
    template?: string;
    sentAt?: string;
  }): Promise<OutreachLog> {
    const res = await apiClient.post<OutreachLog>('/admin/crm/outreach', {
      ...input,
      source: 'manual',
    });
    return unwrap(res, 'Failed to log outreach');
  },

  async updateOutreach(
    id: string,
    patch: { outcome?: OutreachOutcome; message?: string }
  ): Promise<OutreachLog> {
    const res = await apiClient.patch<OutreachLog>(
      `/admin/crm/outreach/${id}`,
      patch
    );
    return unwrap(res, 'Failed to update outreach');
  },

  async deleteOutreach(id: string): Promise<void> {
    const res = await apiClient.delete(`/admin/crm/outreach/${id}`);
    unwrap(res, 'Failed to delete outreach');
  },

  // ---------- Notes ----------
  async listNotes(
    userIdOrParams?:
      | string
      | { userId?: string; contactId?: string; page?: number; limit?: number }
  ): Promise<CrmNote[]> {
    const params =
      typeof userIdOrParams === 'string'
        ? { userId: userIdOrParams }
        : userIdOrParams || ({} as { userId?: string; contactId?: string });
    if (!params.userId && !params.contactId) return [];
    const res = await apiClient.get<Paginated<CrmNote>>(
      `/admin/crm/notes${buildQuery(params)}`
    );
    const page = unwrap(res, 'Failed to load notes');
    return page.items;
  },

  async createNote(input: {
    userId?: string;
    contactId?: string;
    body: string;
  }): Promise<CrmNote> {
    const res = await apiClient.post<CrmNote>('/admin/crm/notes', input);
    return unwrap(res, 'Failed to add note');
  },

  async deleteNote(id: string): Promise<void> {
    const res = await apiClient.delete(`/admin/crm/notes/${id}`);
    unwrap(res, 'Failed to delete note');
  },

  // ---------- Reminders ----------
  async listReminders(
    userIdOrParams?:
      | string
      | {
          userId?: string;
          contactId?: string;
          status?: ReminderStatus;
          dueBefore?: string;
          page?: number;
          limit?: number;
        }
  ): Promise<Reminder[]> {
    const params =
      typeof userIdOrParams === 'string'
        ? { userId: userIdOrParams }
        : userIdOrParams || {};
    const res = await apiClient.get<Paginated<Reminder>>(
      `/admin/crm/reminders${buildQuery(params)}`
    );
    const page = unwrap(res, 'Failed to load reminders');
    return page.items;
  },

  async createReminder(input: {
    userId?: string;
    contactId?: string;
    dueAt: string;
    note?: string;
  }): Promise<Reminder> {
    const res = await apiClient.post<Reminder>('/admin/crm/reminders', input);
    return unwrap(res, 'Failed to set reminder');
  },

  async updateReminder(
    id: string,
    patch: { status?: ReminderStatus; dueAt?: string; note?: string }
  ): Promise<Reminder> {
    const res = await apiClient.patch<Reminder>(
      `/admin/crm/reminders/${id}`,
      patch
    );
    return unwrap(res, 'Failed to update reminder');
  },

  async deleteReminder(id: string): Promise<void> {
    const res = await apiClient.delete(`/admin/crm/reminders/${id}`);
    unwrap(res, 'Failed to delete reminder');
  },

  async completeUserReminders(userId: string): Promise<{ updated: number }> {
    const res = await apiClient.post<{ updated: number }>(
      `/admin/crm/reminders/complete-user/${userId}`,
      {}
    );
    return unwrap(res, 'Failed to complete reminders');
  },

  async completeContactReminders(contactId: string): Promise<{ updated: number }> {
    const res = await apiClient.post<{ updated: number }>(
      `/admin/crm/reminders/complete-contact/${contactId}`,
      {}
    );
    return unwrap(res, 'Failed to complete reminders');
  },

  // ---------- Summary ----------
  async getSummary(): Promise<CrmSummary> {
    const res = await apiClient.get<CrmSummary>('/admin/crm/summary');
    if (!res.success) {
      // Silent failure — this is polled in the background by the admin header.
      throw new Error(res.error?.message || 'Failed to load summary');
    }
    return res.data;
  },

  // ---------- Contacts ----------
  async listContacts(params?: {
    q?: string;
    stage?: string;
    /** @deprecated Use `stage`; left in place so legacy callers still type-check. */
    status?: ContactStatus;
    source?: ContactSource;
    assignedTo?: string;
    page?: number;
    limit?: number;
  }): Promise<Paginated<Contact>> {
    const res = await apiClient.get<Paginated<Contact>>(
      `/admin/crm/contacts${buildQuery(params || {})}`
    );
    return unwrap(res, 'Failed to load contacts');
  },

  async getContact(id: string): Promise<Contact> {
    const res = await apiClient.get<Contact>(`/admin/crm/contacts/${id}`);
    return unwrap(res, 'Failed to load contact');
  },

  async createContact(input: Partial<Contact> & { name: string }): Promise<Contact> {
    const res = await apiClient.post<Contact>('/admin/crm/contacts', input);
    return unwrap(res, 'Failed to create contact');
  },

  async updateContact(id: string, patch: Partial<Contact>): Promise<Contact> {
    const res = await apiClient.patch<Contact>(`/admin/crm/contacts/${id}`, patch);
    return unwrap(res, 'Failed to update contact');
  },

  async assignContact(id: string, assignedTo: string | null): Promise<Contact> {
    const res = await apiClient.patch<Contact>(
      `/admin/crm/contacts/${id}/assign`,
      { assignedTo }
    );
    return unwrap(res, 'Failed to assign contact');
  },

  async deleteContact(id: string): Promise<void> {
    const res = await apiClient.delete(`/admin/crm/contacts/${id}`);
    unwrap(res, 'Failed to delete contact');
  },

  // ---------- Reports ----------
  async getReport(params?: { from?: string; to?: string; scope?: 'own' | 'team' }): Promise<CrmReport> {
    const res = await apiClient.get<CrmReport>(
      `/admin/crm/reports${buildQuery(params || {})}`
    );
    return unwrap(res, 'Failed to load report');
  },

  // ---------- Assignment helpers ----------
  /**
   * Sales-capable admins who can own subscribers. Used by the Assign dropdowns.
   * Reads the CRM endpoint (gated by `crm.contacts.assign`) so sales reps can
   * fetch the roster too — `/admin/users` is super-admin only.
   */
  async listAssignableAdmins(): Promise<AdminUser[]> {
    try {
      const res = await apiClient.get<{
        items: Array<{ userId: string; name: string; email: string; roles: string[] }>;
      }>('/admin/crm/assignable-admins');
      if (!res.success) return [];
      return (res.data.items ?? []).map((a) => {
        // Collapse the role list to a single representative role so the UI's
        // elevated-admin filter (super_admin/admin) keeps working.
        const role = a.roles?.includes('super_admin')
          ? 'super_admin'
          : a.roles?.includes('admin')
          ? 'admin'
          : a.roles?.[0] || 'sales_admin';
        return {
          userId: a.userId,
          name: a.name,
          email: a.email,
          role,
          isVerified: true,
          subscriptionStatus: null,
          subscriptionPlan: null,
          createdAt: '',
        } as AdminUser;
      });
    } catch {
      return [];
    }
  },
};
